/**
 * src/components/spatial/eventManagementModel.ts
 *
 * What an operator can do to an event, and what its machinery is
 * doing, said in words a person reads.
 *
 * Two jobs, both about presentation rather than permission.
 *
 * THE ACTIONS. An operator should be able to maintain an event without
 * reading metadata. The Phase 5 route surface already has the verbs;
 * this names them, says what each one means, and marks the ones that
 * cannot apply right now — you cannot resume an event that is not
 * paused, and offering the control anyway teaches people that buttons
 * lie.
 *
 * THE MACHINERY. An execution rule is currently legible only as a
 * contract address and a function name, which describes the plumbing
 * rather than the thing. The order here is deliberate:
 *
 *     action → availability → condition → function
 *
 * "Buy a ticket", "not yet — starts in two hours", "within 50 m of the
 * entrance", and only then `buy_ticket` on `CTICKETS…`. The
 * identifiers stay reachable, because somebody debugging a rule needs
 * them, but they are the last line rather than the first.
 *
 * WHAT THIS IS NOT. It is not authorization. Every action listed here
 * is still authorized on the server against the event's own authority
 * model, and a control this file decides to show is a control the
 * server may still refuse. Hiding a button is a courtesy to the
 * person, never a security boundary — the comment is here because the
 * opposite assumption is how UI-gated systems get broken into.
 */

import type { EventLifecycleVerb } from '../../services/spatial/spatialEventsClient';
import type { EventActionDescriptor, EventSummary } from './eventSummaryModel';

/** Everything an operator can ask for. Mirrors the Phase 5 routes. */
export type EventOperatorAction =
  | 'edit' | 'reschedule' | 'relocate'
  | EventLifecycleVerb
  | 'duplicate'
  | 'manage_children' | 'manage_broadcasts'
  | 'inspect_rules' | 'inspect_contracts';

export interface EventOperatorControl {
  action: EventOperatorAction;
  label: string;
  /** One line saying what it does, for a person who has not done it before. */
  detail: string;
  /** Whether it applies to the event as it is right now. */
  applicable: boolean;
  /** Why not, when it does not apply. Shown rather than hidden. */
  unavailableReason: string | null;
  /**
   * Whether this reaches past the event into somebody else's property.
   *
   * Contract and rule attachment point machinery at a venue, so the
   * server additionally requires the host's authority. Flagged here so
   * the UI can say "the venue has to agree" before somebody fills in a
   * form that will be refused.
   */
  needsHostAuthority: boolean;
  /** Whether it cannot be undone, so the UI can confirm first. */
  destructive: boolean;
}

const PAUSED = 'paused';
const DRAFT = 'draft';

/**
 * The controls for an event in its current state.
 *
 * Everything is listed, including what does not apply, because a
 * disappearing control is indistinguishable from a missing feature.
 * `applicable: false` with a reason is the honest version.
 */
export function operatorControlsFor(summary: EventSummary | null): EventOperatorControl[] {
  const lifecycle = summary?.lifecycle ?? null;
  const concluded = !!summary?.concluded;
  const isPaused = lifecycle === PAUSED;
  const isDraft = lifecycle === DRAFT;

  const over = (label: string) => (concluded
    ? `${label} is over, so this no longer applies`
    : null);

  const controls: EventOperatorControl[] = [
    {
      action: 'edit',
      label: 'Edit details',
      detail: 'Title, description, image, tags, capacity, who is hosting.',
      applicable: !concluded,
      unavailableReason: over('The event'),
      needsHostAuthority: false,
      destructive: false,
    },
    {
      action: 'reschedule',
      label: 'Change the time',
      detail: 'Move the start or end. The map and the rules follow the new times.',
      applicable: !concluded,
      unavailableReason: over('The event'),
      needsHostAuthority: false,
      destructive: false,
    },
    {
      action: 'relocate',
      label: 'Change the place',
      detail: 'Move the event area, or name a different floor or zone inside the venue.',
      applicable: !concluded,
      // Worth stating plainly: this moves the event, not the building.
      unavailableReason: concluded ? over('The event') : null,
      needsHostAuthority: false,
      destructive: false,
    },
    {
      action: 'pause',
      label: 'Pause',
      detail: 'Stop presenting it as on, without cancelling. Check-ins are kept.',
      applicable: !concluded && !isPaused && !isDraft,
      unavailableReason: concluded
        ? over('The event')
        : (isPaused ? 'Already paused' : (isDraft ? 'Not published yet' : null)),
      needsHostAuthority: false,
      destructive: false,
    },
    {
      action: 'resume',
      label: 'Resume',
      detail: 'Put it back on the schedule.',
      applicable: isPaused,
      unavailableReason: isPaused ? null : 'Only a paused event can resume',
      needsHostAuthority: false,
      destructive: false,
    },
    {
      action: 'cancel',
      label: 'Cancel',
      detail: 'It is not happening. It stops showing as on and stays in the record.',
      applicable: !concluded,
      unavailableReason: over('The event'),
      needsHostAuthority: false,
      destructive: true,
    },
    {
      action: 'complete',
      label: 'Mark finished',
      detail: 'It happened and is over. Attendance is kept as history.',
      applicable: !concluded && !isDraft,
      unavailableReason: concluded
        ? 'Already finished'
        : (isDraft ? 'Not published yet' : null),
      needsHostAuthority: false,
      destructive: true,
    },
    {
      action: 'archive',
      label: 'Archive',
      detail: 'Remove it from live views. It stays findable and its records stay.',
      applicable: lifecycle !== 'archived',
      unavailableReason: lifecycle === 'archived' ? 'Already archived' : null,
      needsHostAuthority: false,
      destructive: true,
    },
    {
      action: 'duplicate',
      label: 'Duplicate',
      detail: 'Start a new draft from this one. Contracts and rules are not carried over.',
      // Always applicable: copying a finished event to run it again is
      // the normal way a recurring event gets made.
      applicable: true,
      unavailableReason: null,
      needsHostAuthority: false,
      destructive: false,
    },
    {
      action: 'manage_children',
      label: 'Sessions inside this event',
      detail: 'Add or open the talks, sets or rooms that belong to it.',
      applicable: !concluded,
      unavailableReason: over('The event'),
      // A child event points at the same venue, so the venue's
      // agreement is part of the answer.
      needsHostAuthority: true,
      destructive: false,
    },
    {
      action: 'manage_broadcasts',
      label: 'Broadcasts',
      detail: 'What is on air, what has been, and what was recorded.',
      applicable: true,
      unavailableReason: null,
      needsHostAuthority: false,
      destructive: false,
    },
    {
      action: 'inspect_rules',
      label: 'Rules behind the actions',
      detail: 'What each action needs before it can run.',
      applicable: true,
      unavailableReason: null,
      // Reading is not attaching. Only changing the attachment needs
      // the host.
      needsHostAuthority: false,
      destructive: false,
    },
    {
      action: 'inspect_contracts',
      label: 'Contracts',
      detail: 'The functions an action calls, and what it costs.',
      applicable: true,
      unavailableReason: null,
      needsHostAuthority: false,
      destructive: false,
    },
  ];

  return controls;
}

// ── Rules and contracts, in words ───────────────────────────────────

export interface RuleExplanation {
  /** What it is for. The first thing said. */
  action: string;
  /** Whether it can run right now, and why not. */
  availability: { ok: boolean; reason: string };
  /** What has to be true. Split, because the two fail differently. */
  conditions: { spatial: string | null; temporal: string | null };
  /** What it calls. Said in terms of the function, not the address. */
  functionName: string | null;
  /** Whether somebody has to sign, and what it costs. */
  signing: { required: boolean; cost: string };
  /**
   * The identifiers, last.
   *
   * Reachable for somebody debugging a rule and never the headline. A
   * contract address is not what the action is.
   */
  technical: {
    executionRuleId: string | null;
    contractName: string | null;
    contractAddress: string | null;
    network: string | null;
    parameters: string | null;
  };
}

/** Refusal reasons the gate produces, said in words a person reads. */
const REASON_TEXT: Readonly<Record<string, string>> = Object.freeze({
  within_event_window: 'Available now',
  event_not_started: 'Not yet — the event has not started',
  event_ended: 'The event has ended',
  event_cancelled: 'The event was cancelled',
  event_inactive: 'The event is not running',
  outside_radius: 'You are too far away',
  dwell_too_short: 'You need to stay a little longer',
  rule_inactive: 'This is switched off',
  rule_not_found: 'The rule behind this is missing',
  eligibility_unknown: 'Cannot tell right now',
  no_wallet: 'Connect a wallet first',
});

function _metres(value: number | null | undefined): string | null {
  if (!Number.isFinite(value as number)) return null;
  const m = Math.round(value as number);
  return m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${m} m`;
}

function _duration(seconds: number | null | undefined): string | null {
  if (!Number.isFinite(seconds as number) || (seconds as number) <= 0) return null;
  const s = Math.round(seconds as number);
  if (s < 60) return `${s} seconds`;
  const minutes = Math.round(s / 60);
  return minutes === 1 ? '1 minute' : `${minutes} minutes`;
}

/**
 * Explain one action the way a person would ask about it.
 *
 * Everything here is read from the server's descriptor. The reason
 * text is the only thing invented, and only because a gate reason is a
 * machine word — mapping it is presentation, and an unmapped reason
 * falls through as itself rather than being hidden, so a new refusal
 * shows up as something to translate instead of vanishing.
 */
export function explainAction(action: EventActionDescriptor | null | undefined): RuleExplanation | null {
  if (!action) return null;

  const reason = action.eligibility?.reason || null;
  const passing = !!action.eligibility?.passing;

  const spatial = action.conditions?.spatial || null;
  const temporal = action.conditions?.temporal || null;

  const radius = _metres(spatial?.radiusMeters);
  const dwell = _duration(spatial?.minDwellSeconds);
  const spatialText = radius
    ? (dwell ? `Within ${radius}, after ${dwell} there` : `Within ${radius}`)
    : (dwell ? `After ${dwell} in place` : null);

  let temporalText: string | null = null;
  if (temporal?.startsAt) {
    temporalText = temporal.endsAt
      ? `Between ${temporal.startsAt} and ${temporal.endsAt}`
      : `From ${temporal.startsAt}`;
  } else if (temporal?.state) {
    temporalText = `While the event is ${temporal.state}`;
  }

  const cost = action.cost;
  const costText = cost
    ? (cost.known === false
      ? (cost.detail || 'Cost is shown in your wallet before you sign')
      : 'Free')
    : 'No cost';

  return {
    action: action.label || action.verb || 'Action',
    availability: {
      ok: passing,
      reason: (reason && REASON_TEXT[reason])
        || (passing ? 'Available now' : reason || 'Not available'),
    },
    conditions: { spatial: spatialText, temporal: temporalText },
    functionName: action.contract?.functionName || null,
    signing: { required: !!action.requiresSigning, cost: costText },
    technical: {
      executionRuleId: action.executionRuleId != null
        ? String(action.executionRuleId)
        : null,
      contractName: action.contract?.name || null,
      contractAddress: action.contract?.address || null,
      network: action.contract?.network || null,
      // Described, never disclosed: the argument mapping is withheld
      // on the server and this repeats what it said about itself.
      parameters: action.contract?.parameters?.known === false
        ? (action.contract.parameters.detail || 'Shown in your wallet before you sign')
        : null,
    },
  };
}

/**
 * Whether an action is worth showing its technical detail for.
 *
 * An interaction verb with no contract behind it has nothing to
 * expand, and an empty "technical details" panel reads as something
 * withheld.
 */
export function hasTechnicalDetail(explanation: RuleExplanation | null): boolean {
  if (!explanation) return false;
  const t = explanation.technical;
  return !!(t.executionRuleId || t.contractAddress || t.contractName || explanation.functionName);
}
